import { Page, Pageable, PaginatedData, Response } from '@/types/common/Rest';
import { AxiosResponse } from 'axios';
import { DataOptions } from 'vuetify';
import { remoteError } from './remoteError';
import { VuetifyPageable } from '@/types/common/vuetify';

export const formatSortingString = (
  pageable: Pageable,
  defaultSorting?: string
): string | undefined => {
  if (pageable.sort) {
    return `${pageable.sort},${pageable.direction}`;
  }
  return defaultSorting;
};

export const vuetifyDataOptionsToPageable = (dataOptions: DataOptions) => {
  const page = dataOptions.page - 1;
  const size = dataOptions.itemsPerPage;
  const sort = dataOptions.sortBy.length > 0 ? dataOptions.sortBy[0] : '';
  const direction =
    dataOptions.sortDesc.length > 0
      ? dataOptions.sortDesc[0]
        ? 'desc'
        : 'asc'
      : '';

  return { page, size, sort, direction } as Pageable;
};

export const pageableToVuetifyDataOptions = (pageable: Pageable) => {
  return {
    page: pageable.page + 1,
    itemsPerPage: pageable.size,
    sortBy: [pageable.sort],
    sortDesc: [pageable.direction === 'desc' ? true : false],
  } as DataOptions;
};

export const vuetifyPageableToPageable = (
  vuetifyPageable?: VuetifyPageable
) => {
  const page = (vuetifyPageable?.page ?? 1) - 1;
  const size = vuetifyPageable?.itemsPerPage;
  const sortDirection =
    (vuetifyPageable?.sortDesc?.length ?? 0) > 0
      ? vuetifyPageable?.sortDesc[0]
        ? 'desc'
        : 'asc'
      : '';
  const sort =
    (vuetifyPageable?.sortBy?.length ?? 0) > 0
      ? `${vuetifyPageable?.sortBy[0]},${sortDirection}`
      : '';

  return {
    page,
    size,
    sort,
  } as Pageable;
};

export function convertToPage<T>(
  data: PaginatedData<T>,
  pageable: Pageable
): Page<T> {
  return {
    data: data.content!,
    pageable: pageable!,
    totalPages: data.totalPages!,
    totalElements: data.totalElements!,
  };
}

export const newResponse = <T>(): Response<T> => {
  return new Response<T>();
};

export const handleApiResponse = async <T>(
  requestFn: () => Promise<AxiosResponse<T>>
): Promise<Response<T>> => {
  const response = newResponse<T>();

  try {
    const { data } = await requestFn();
    response.content = data;
  } catch (e) {
    response.error = remoteError(e);
  }

  return response;
};

export const handlePageApiResponse = async <T>(
  pageable: Pageable,
  requestFn: () => Promise<AxiosResponse<PaginatedData<T>>>
): Promise<Response<Page<T>>> => {
  const response = new Response<Page<T>>();
  try {
    const { data } = await requestFn();
    const page: Page<T> = convertToPage(data, pageable);
    response.content = page;
  } catch (e) {
    response.error = remoteError(e);
  }
  return response;
};
